import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, CheckCheck, Settings as SettingsIcon } from "lucide-react";
import {
  NOTIFICATION_CATEGORY_LABEL,
  notificationKindCategory,
  notificationKindLabel,
} from "@app/shared";
import { api, type AppNotification } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/format";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { cn } from "@/lib/utils";

/**
 * M23b — the outbox, readable.
 *
 * This page exists because the system already promised it. When a boot drain suppresses a
 * backlog it tells Andy in Telegram that the rows are "readable in the app", and until now
 * there was nowhere to read them: the only view of a notification was the Telegram message,
 * which is precisely what failed in the case the message is about.
 *
 * So the design rule here is the inverse of the Telegram channel's: **a notification that was
 * never sent is the most important thing on this page, not a footnote.** Its status is shown,
 * with the reason, rather than hidden behind a healthy-looking list.
 *
 * Opening this page does NOT mark anything read. A badge that clears itself on a glance
 * cannot be used to keep something flagged for later, which is most of what a badge is for.
 */

const PAGE = 30;

export function Notifications() {
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [pages, setPages] = useState(1);

  const limit = PAGE * pages;
  const params = { limit, unread: unreadOnly ? true : undefined };

  const query = useQuery({
    queryKey: queryKeys.notifications(params),
    queryFn: () => api.notifications(params),
  });

  /**
   * Captured when the list renders, not when the button is pressed.
   *
   * "Mark all read" must not swallow something that arrived while Andy was reading — the row
   * he has not seen is always the newest one, and it is the only one a blanket sweep can
   * silently lose.
   */
  const renderedAt = useMemo(() => new Date().toISOString(), [query.dataUpdatedAt]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  const markAll = useMutation({
    mutationFn: () => api.markAllNotificationsRead(renderedAt),
    onSuccess: invalidate,
  });

  const toggleRead = useMutation({
    mutationFn: ({ id, read }: { id: number; read: boolean }) => api.markNotificationRead(id, read),
    onSuccess: invalidate,
  });

  if (query.isLoading) return <Skeleton className="h-64 w-full" />;
  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">Could not load notifications.</p>;
  }

  const { notifications, nextCursor, unreadCount } = query.data;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">
          Notifications
          {unreadCount > 0 && <span className="ml-2 text-sm text-muted-foreground">{unreadCount} unread</span>}
        </h1>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            disabled={unreadCount === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            <CheckCheck className="h-4 w-4" /> Mark all read
          </Button>
          {/* M26c — the way to "what gets sent" from the page the bell leads to, instead of
              only via the Settings tab. */}
          <Link
            to="/notifications/settings"
            aria-label="Notification settings"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
          >
            <SettingsIcon className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <EnablePushCard />

      <div className="flex flex-wrap items-center gap-1.5">
        {[
          { label: "All", value: false },
          { label: "Unread", value: true },
        ].map((option) => (
          <button
            key={option.label}
            type="button"
            onClick={() => {
              setUnreadOnly(option.value);
              setPages(1);
            }}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs transition-colors",
              option.value === unreadOnly
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border text-muted-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {notifications.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {unreadOnly ? "Nothing unread." : "Nothing has been recorded yet."}
        </p>
      )}

      <div className="space-y-2">
        {notifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            // Per row, not the mutation's global flag: marking one row read must not grey
            // out every other row's button for the duration of the round trip.
            busy={toggleRead.isPending && toggleRead.variables?.id === notification.id}
            onToggleRead={() =>
              toggleRead.mutate({ id: notification.id, read: notification.readAt === null })
            }
          />
        ))}
      </div>

      {nextCursor !== null && (
        <Button variant="outline" className="w-full" onClick={() => setPages((current) => current + 1)}>
          Load more
        </Button>
      )}
    </div>
  );
}

/**
 * M26c — the one push action that matters, on the page the bell already leads to.
 *
 * The subscribe flow lived at the bottom of Settings → "What gets sent", three levels deep —
 * which in practice meant it did not exist: push was deployed server-side and the phone was
 * never enrolled because nothing said where. This renders ONLY when pressing it would work:
 * server configured, browser capable, permission not refused, this device not subscribed,
 * and the probe answered (`checked` — a banner that flashes at every subscribed visitor for
 * a frame trains them to ignore it).
 *
 * The subscribe call runs in the click handler because `Notification.requestPermission()`
 * requires the user gesture — the same constraint documented on PushDevices.
 */
function EnablePushCard() {
  const { supported, checked, denied, endpoint, config, subscribe } = usePushSubscription();

  const actionable =
    supported && checked && !denied && endpoint === null && config.data?.configured === true;
  if (!actionable) return null;

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardContent className="flex items-center justify-between gap-3 p-3">
        <p className="text-xs text-muted-foreground">
          Push is off on this device — nothing can reach you when the app is closed.
        </p>
        <Button size="sm" disabled={subscribe.isPending} onClick={() => subscribe.mutate()}>
          <BellRing className="h-4 w-4" /> {subscribe.isPending ? "Asking…" : "Enable push"}
        </Button>
      </CardContent>
    </Card>
  );
}

function NotificationCard({
  notification,
  busy,
  onToggleRead,
}: {
  notification: AppNotification;
  busy: boolean;
  onToggleRead: () => void;
}) {
  const unread = notification.readAt === null;
  const category = notificationKindCategory(notification.kind);

  return (
    // M32 — the whole card is the tap target, one direction only: a tap marks an unread row
    // read and does nothing to a read one, so an accidental brush cannot re-flag something
    // already dealt with. The small button stays the two-way switch. This is a deliberate
    // click-target on a div, not a missing <button>: making the card a button would nest the
    // Read/Unread button inside another button, which is invalid and breaks screen readers —
    // they get the same action through that inner button, which keeps its accessible name.
    <Card
      className={cn(unread && "cursor-pointer border-primary/40")}
      onClick={unread && !busy ? onToggleRead : undefined}
    >
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
              <Badge variant="outline">{notificationKindLabel(notification.kind)}</Badge>
              {category && (
                <span className="text-xs text-muted-foreground">
                  {NOTIFICATION_CATEGORY_LABEL[category]}
                </span>
              )}
            </div>
            {/* Plain text, from the server. Rendering the Telegram HTML here would mean
                trusting channel-authored strings in the DOM for the sake of some bold. */}
            <p className="whitespace-pre-wrap break-words text-sm">{notification.plain}</p>
          </div>
          <button
            type="button"
            onClick={(event) => {
              // Without this, a press on the button also bubbles into the card's handler
              // above and the same mutation fires twice.
              event.stopPropagation();
              onToggleRead();
            }}
            disabled={busy}
            // The visible word is the action; the accessible name says which row it acts on,
            // because "Unread" is also the name of the filter above it.
            aria-label={unread ? "Mark this read" : "Mark this unread"}
            className="shrink-0 text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            {unread ? "Read" : "Unread"}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{formatDateTime(notification.createdAt)}</span>
          <span>· {notification.target}</span>
          {notification.status !== "sent" && <StatusBadge status={notification.status} />}
        </div>

        {/* The reason a message never reached Telegram is the single most useful line on this
            page, so it is shown rather than folded into the badge above. For a muted row that
            reason names the switch that muted it, so the page says what to do about it. */}
        {notification.lastError && (
          <p className="break-words text-xs text-muted-foreground">{notification.lastError}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Three ways not to be sent, and they are not interchangeable.
 *
 * `suppressed` is a choice Andy made; showing it as "not delivered" in red would report his
 * own setting back to him as a defect, on the one page whose job is to be trusted about
 * what did and did not go out. `pending` still intends to send. Only `failed` is a fault.
 */
function StatusBadge({ status }: { status: AppNotification["status"] }) {
  if (status === "suppressed") return <Badge variant="outline">muted</Badge>;
  if (status === "failed") return <Badge variant="destructive">not delivered</Badge>;
  return <Badge variant="secondary">queued</Badge>;
}
